/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import PropTypes from 'prop-types';
import { ShieldAlert, CheckCircle, AlertCircle, Clock, WifiOff, FileSearch } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ERROR_CONFIGS — single source of truth for all Capture error states.
// To update a message or title, edit only this object.
// ─────────────────────────────────────────────────────────────────────────────
export const ERROR_CONFIGS = {
  // HTTP 410 — assignment passed its expiry date
  expired: {
    title: 'Link Expired',
    message: 'This link has expired. Please contact the sender to request a new link.',
    icon: 'expired',
    iconColor: '#f97316',
  },
  // HTTP 410 — assignment was already fully submitted
  completed: {
    title: 'Already Submitted',
    message: 'This request has already been submitted.',
    icon: 'completed',
    iconColor: '#22c55e',
  },
  // HTTP 410 — link was opened on a different device
  different_device: {
    title: 'Link Opened Elsewhere',
    message: 'This link was already opened on another device.',
    icon: 'forbidden',
    iconColor: '#ef4444',
  },
  // HTTP 410 — assignment was soft-deleted by the creator
  deleted: {
    title: 'Link No Longer Available',
    message: 'This link is no longer available.',
    icon: 'notFound',
    iconColor: '#6b7280',
  },
  // HTTP 404 — token / short URL does not exist in the database
  not_found: {
    title: 'Link Not Found',
    message: 'The link you are trying to access is no longer valid.',
    icon: 'notFound',
    iconColor: '#6b7280',
  },
  // No network detected
  offline: {
    title: 'No Internet Connection',
    message: 'No internet connection. Please check and try again.',
    icon: 'offline',
    iconColor: '#6b7280',
  },
  // Session errors
  session_error: {
    title: 'Already in use',
    message: 'Your session has encountered an error. Please reload the page to continue.',
    icon: 'forbidden',
    iconColor: '#ef4444',
  },
  // HTTP 500 or any unhandled error
  default: {
    title: 'Unexpected error',
    message: 'Something went wrong. Please try again.',
    icon: 'error',
    iconColor: '#ef4444',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal icon map — keys match the icon strings used in ERROR_CONFIGS above
// ─────────────────────────────────────────────────────────────────────────────
const ErrorScreen = ({ title, message, icon = 'error', iconColor, hint, action, branding }) => {
  // Internal icon map — keys match the icon strings used in ERROR_CONFIGS above
  const ICONS = {
    error: AlertCircle,
    forbidden: ShieldAlert,
    completed: CheckCircle,
    expired: Clock,
    offline: WifiOff,
    notFound: FileSearch,
  };

  const Icon = ICONS[icon] || ICONS.error;
  // Use per-config iconColor, then branding primaryColor, then default red
  const resolvedColor = branding?.primaryColor || iconColor || ERROR_CONFIGS[icon]?.iconColor || '#ef4444';

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-sm text-center">

        {/* Icon */}
        <div className="flex justify-center mb-3">
          <Icon size={32} strokeWidth={2.5} color={resolvedColor} />
        </div>

        {/* Title */}
        <h1 className="text-base font-semibold text-gray-900 mb-1">
          {title}
        </h1>

        {/* Message */}
        <p className="text-sm text-gray-500">
          {message}
        </p>

        {/* Optional hint */}
        {hint && (
          <p className="text-xs text-gray-400 mt-2">{hint}</p>
        )}

        {/* Optional action button */}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-5 px-5 py-2 bg-primary rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 active:scale-95"

          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorScreen;

ErrorScreen.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  hint: PropTypes.string,
  action: PropTypes.shape({
    onClick: PropTypes.func,
    label: PropTypes.string,
  }),
  branding: PropTypes.shape({
    primaryColor: PropTypes.string,
  }),
};
